export function getBrowserInfo() {
    let ua = navigator.userAgent;
    let temp;
    let browserInfo = {
        name: "Unknown",
        version: "Unknown"
    };

    temp = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i)
    if (temp) {
        if (/trident/i.test(temp[1])) {
            // for IE
            let rv = /\brv[ :]+(\d+)/g.exec(ua) || [];
            return { name: "IE", version: (rv[1] || "") };
        }
        if (temp[1] === "Chrome") {
            // check if it's actually new Edge, masquerading as Chrome
            let temp2 = ua.match(/\b(OPR|Edge(?=\/))\/(\d+)/);
            if (temp2 != null) return { name: temp2[1].replace("OPR", "Opera"), version: temp2[2] };
        }
        browserInfo.name = temp[1];
        browserInfo.version = temp[2];
    } else {
        // for older browsers, like Netscape
        temp = ua.match(/version\/(\d+)/i)
        if (temp) {
            browserInfo.version = temp[1];
        }
    }

    return browserInfo;
}

export function sanitizeInput(input) {
    if (!input || typeof input !== "string") {
        return "";
    }

    // Limit the input to 5000 characters
    let sanitizedInput = input.slice(0, 5000);

    // Remove any script tags
    sanitizedInput = sanitizedInput.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

    // Escape special characters
    sanitizedInput = sanitizedInput
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;');

    // Replace any encoded JavaScript "event:" strings (like event:alert)
    sanitizedInput = sanitizedInput.replace(/event:/gi, '');

    // Further security measures can be implemented depending on the exact use case.
    // Consider libraries like DOMPurify for a more comprehensive solution.

    return sanitizedInput;
}

export function isEmpty(obj) {
    return Object.keys(obj).length === 0;
}
export function getAbsoluteSemester(semester, referenceSemesterStr) {
    // Extract the reference semester number and handle 's' and 'w' prefixes
    const reference = referenceSemesterStr[0] === "s"
        ? parseInt(referenceSemesterStr.slice(1), 10)  // If it's "s", just parse the number
        : parseInt(referenceSemesterStr.slice(1), 10) + 0.5;  // If it's "w", add 0.5

    // Calculate the absolute semester, adjusting for the base reference semester
    const absoluteSemester = reference + (semester - 1) / 2;

    // Determine if the semester is 's' (integer) or 'w' (half)
    const absoluteSemesterStr = `${Number.isInteger(absoluteSemester) ? "s" : "w"}${Math.floor(absoluteSemester)}`;

    return absoluteSemesterStr;
}

export function getSemesterDifference(firstSemStr, secondSemStr) {
    /**
     * Returns the semester difference between the first and second argument semester strings.
     * Positive if the first semester is later than the second.
     */
    const pattern = /^[sw]\d{2}$/;  // Define the pattern to match semester strings

    [firstSemStr, secondSemStr].forEach(string => {
        if (!pattern.test(string)) {
            console.log(string);
            throw new Error(`Reference semester input format invalid: ${string}. Must adhere to the pattern ${pattern}`);
        }
    });

    const reference = secondSemStr[0] === 's'
        ? parseInt(secondSemStr.substring(1))
        : parseInt(secondSemStr.substring(1)) + 0.5;

    const semester = firstSemStr[0] === 's'
        ? parseInt(firstSemStr.substring(1))
        : parseInt(firstSemStr.substring(1)) + 0.5;

    return Math.round((semester - reference) * 2);
}
export function currentSemester(preferences) {
    if (!preferences?.currentSemester || !preferences?.startSemester) return 1;
    return getSemesterDifference(preferences.currentSemester, preferences.startSemester) + 1;
}
export function formatSemester(semesterCode) {

    if (semesterCode === 'Current') {
        return 'Current Semester';
    }
    if (typeof semesterCode === 'string' && /^[ws]\d{2}$/.test(semesterCode)) {
        const season = semesterCode.startsWith('w') ? 'winter semester' : 'summer semester';
        const year = `20${semesterCode.slice(1)}`;
        return `${season} ${year}`;
    }
    return 'Semester ' + semesterCode;
}

export function toTitleCase(text) {
    const minorWords = [
        'a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor',
        'on', 'in', 'at', 'to', 'from', 'by', 'with', 'of'
    ];

    if (!text) return '';

    return text
        .toLowerCase()
        .split(' ')
        .map((word, index) => {
            if (index === 0 || !minorWords.includes(word)) {
                return word.charAt(0).toUpperCase() + word.slice(1);
            }
            return word;
        })
        .join(' ');
}

export function getExplanatoryUnavailableReason(module, unavailableReason = "not offered", targetSemester = null, startSemester = null) {
    if (unavailableReason === "required") {
        return {
            full: "The module is required in your study program",
            short: "required module"
        };
    }

    if (unavailableReason && unavailableReason !== "not offered" && unavailableReason !== "inactive") {
        const capitalized = unavailableReason.charAt(0).toUpperCase() + unavailableReason.slice(1);
        return {
            full: capitalized,
            short: unavailableReason.toLowerCase()
        };
    }

    const validTo = module?.validTo || module?.valid_to;
    if (validTo) {
        let refSem = null;
        if (targetSemester !== null && targetSemester !== undefined && startSemester && typeof startSemester === 'string' && /^[ws]\d{2}$/.test(startSemester)) {
            try {
                const semNum = typeof targetSemester === 'number' ? targetSemester : parseInt(targetSemester, 10);
                if (!isNaN(semNum)) {
                    refSem = getAbsoluteSemester(semNum, startSemester);
                }
            } catch (e) {
                // ignore
            }
        } else if (typeof targetSemester === 'string' && /^[ws]\d{2}$/.test(targetSemester)) {
            refSem = targetSemester;
        }
        if (!refSem) {
            refSem = startSemester;
        }
        if (refSem && typeof refSem === 'string' && /^[ws]\d{2}$/.test(refSem) && typeof validTo === 'string' && /^[ws]\d{2}$/.test(validTo)) {
            try {
                if (getSemesterDifference(refSem, validTo) > 0) {
                    return {
                        full: "No longer offered",
                        short: "no longer offered"
                    };
                }
            } catch (e) {
                // ignore
            }
        }
    }

    const occ = module?.occurrence;

    //let targetSeason = null;
    if (targetSemester !== null && targetSemester !== undefined && startSemester && typeof startSemester === 'string' && /^[ws]\d{2}$/.test(startSemester)) {
        try {
            const semNum = typeof targetSemester === 'number' ? targetSemester : parseInt(targetSemester, 10);
            if (!isNaN(semNum)) {
                const absSem = getAbsoluteSemester(semNum, startSemester);
                if (absSem && (absSem.startsWith('w') || absSem.startsWith('s'))) {
                    //targetSeason = absSem[0];
                }
            }
        } catch (e) {
            // ignore
        }
    } else if (typeof targetSemester === 'string' && /^[ws]\d{2}$/.test(targetSemester)) {
        //targetSeason = targetSemester[0];
    }

    if (occ === 'w') {
        /*if (targetSeason === 's') {
            return {
                full: "Not offered in summer semesters",
                short: "not offered in summer semesters"
            };
        }*/
        return {
            full: "Offered only in winter semesters",
            short: "offered only in winter semesters"
        };
    }

    if (occ === 's') {
        /*if (targetSeason === 'w') {
            return {
                full: "Not offered in winter semesters",
                short: "not offered in winter semesters"
            };
        }*/
        return {
            full: "Offered only in summer semesters",
            short: "offered only in summer semesters"
        };
    }

    if (occ === 'w/s' || occ === 's/w') {
        return {
            full: "Not offered in this semester",
            short: "not offered in this semester"
        };
    }

    return {
        full: "Offered only irregularly",
        short: "offered only irregularly"
    };
}